import { toCamelCase, WSText } from "@wingspanhq/fe-component-library";
import {
  IClientInvoice,
  PaymentMethod
} from "@wingspanhq/payments/dist/interfaces";
import React from "react";
import { Redirect, RouteComponentProps, useHistory } from "react-router-dom";
import { CertifiedSecureDigitalPayments } from "../../components/CertifiedSecureDigitalPayments/CertifiedSecureDigitalPayments";
import { getClientInvoiceMemberName } from "../../query/payments/selectors";
import { ClientInvoiceWrapper } from "../components/ClientInvoiceWrapper/ClientInvoiceWrapper";
import { ClientPaymentsWrapper } from "../components/ClientPaymentsWrapper/ClientPaymentsWrapper";
import {
  PaymentType,
  PaymentTypeProps
} from "../components/PaymentType/PaymentType";
import {
  getAcceptedPaymentMethods,
  getPathToPaymentMethod,
  getPaymentStepText,
  isClientInvoiceCancelled,
  isClientInvoicePaid
} from "../utils";

type Props = RouteComponentProps<{ invoiceId: string }>;

const getPaymentTypeProps = (
  paymentMethod: PaymentMethod,
  clientInvoice: IClientInvoice
): PaymentTypeProps =>
  paymentMethod === PaymentMethod.ACH
    ? {
        icon: "institution",
        title: "Direct ACH",
        description: "Simplest, cheapest",
        feeText: "Free",
        badge: {
          text: "Recommended",
          textProps: {
            color: "green500"
          },
          backgroundColor: "green100"
        }
      }
    : paymentMethod === PaymentMethod.Credit
    ? {
        icon: "creditCard",
        title: "Credit card",
        description: "Earn points, defer payment",
        feeText:
          clientInvoice.creditFeeHandling?.memberPays === 100 ? "Free" : "2.9%",
        badge: {
          text: "Fastest"
        }
      }
    : {
        icon: "paperAirplane",
        title: "Wire / Manual ACH",
        description: "Use other bill pay software",
        feeText: "Free"
      };

export const ClientPaymentsInvoicePaymentType: React.FC<Props> = ({
  match
}) => {
  const history = useHistory();
  const invoiceId = match.params.invoiceId;

  return (
    <ClientInvoiceWrapper invoiceId={invoiceId}>
      {clientInvoice => {
        const paymentMethods = getAcceptedPaymentMethods(clientInvoice);

        return isClientInvoicePaid(clientInvoice) ? (
          <Redirect to={`/payment/${invoiceId}/success`} />
        ) : isClientInvoiceCancelled(clientInvoice) ? (
          <Redirect to={`/payment/${invoiceId}`} />
        ) : paymentMethods.length === 1 ? (
          <Redirect to={getPathToPaymentMethod(invoiceId, paymentMethods[0])} />
        ) : (
          <ClientPaymentsWrapper
            companyName={clientInvoice.memberCompany}
            clientEmail={clientInvoice.memberClient.emailTo}
            memberName={getClientInvoiceMemberName(clientInvoice)}
            companyLogoUrl={clientInvoice.memberLogoUrl}
            onBack={() => {
              history.push(`/payment/${invoiceId}`);
            }}
          >
            <WSText.Label>
              {getPaymentStepText(clientInvoice, "paymentType")}
            </WSText.Label>
            <WSText.Heading2 mb="L">Payment method</WSText.Heading2>

            {getAcceptedPaymentMethods(clientInvoice).map(paymentMethod => (
              <PaymentType
                key={paymentMethod}
                mb="S"
                onClick={() => {
                  history.push(
                    getPathToPaymentMethod(invoiceId, paymentMethod)
                  );
                }}
                data-testid={toCamelCase(paymentMethod, "button")}
                {...getPaymentTypeProps(paymentMethod, clientInvoice)}
              />
            ))}

            <CertifiedSecureDigitalPayments />
          </ClientPaymentsWrapper>
        );
      }}
    </ClientInvoiceWrapper>
  );
};
